import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"
import {TransformMaterialSlotType} from "#template/runtime-graph/nodes/transform-material-slot"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {ImageGenerator, InputMaterialConnection, MaterialGraph} from "@cm/material-nodes"
import {Value, BsdfPrincipled, materialConnection, NormalMap, Color, MaterialConnection} from "@cm/material-nodes"
import {hashObject} from "@cm/utils"

const TD = TypeDescriptors

type ExtractMaterialSlotDescriptorType = {
    material: InletDescriptor<MaterialGraph>
    slot: InletDescriptor<TransformMaterialSlotType>
    imageGeneratorForSlot: OutletDescriptor<ImageGenerator>
    sceneManager: InletDescriptor<ISceneManager>
}

const extractMaterialSlotDescriptor: ExtractMaterialSlotDescriptorType = {
    material: TD.inlet(TD.MaterialGraph),
    slot: TD.inlet(TD.String) as InletDescriptor<TransformMaterialSlotType>,
    imageGeneratorForSlot: TD.outlet(TD.ImageGenerator),
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
}

export function extractMaterialSlot(graph: MaterialGraph, slot: TransformMaterialSlotType): InputMaterialConnection<"color"> {
    for (const node of graph.nodes) {
        if (node instanceof BsdfPrincipled) {
            const input = node.parameters.inputs?.[slot]
            if (slot === TransformMaterialSlotType.Normal) {
                if (!input) {
                    return materialConnection(
                        new Color({
                            parameters: {
                                inputs: {},
                                settings: {
                                    color: {r: 0.5, g: 0.5, b: 1},
                                },
                            },
                        }),
                        "color",
                    )
                }

                const sourceNode = input.source
                if (sourceNode instanceof NormalMap) {
                    const normalMapInputColorParameter = sourceNode.parameters.inputs.color
                    if (!(normalMapInputColorParameter instanceof MaterialConnection)) {
                        throw new Error("Expected material connection for normal map color input")
                    }
                    return normalMapInputColorParameter
                } else {
                    throw new Error("Expected normal map to be a NormalMap")
                }
            } else {
                if (input) {
                    return input
                } else {
                    const parameter = node.parameters.settings?.[slot]
                    return materialConnection(
                        new Color({
                            parameters: {
                                inputs: {},
                                settings: {
                                    color: typeof parameter === "number" ? {r: parameter, g: parameter, b: parameter} : (parameter ?? {r: 0, g: 0, b: 0}),
                                },
                            },
                        }),
                        "color",
                    )
                }
            }
        }
    }

    throw new Error(`Could not find node for slot ${slot} in material graph`)
}

function extractMaterialSlotFn(material: MaterialGraph, slot: TransformMaterialSlotType): ImageGenerator {
    const color = extractMaterialSlot(material, slot).clone({cloneSubNode: () => true})
    return {
        imageNode: {
            generator: () => {
                return {
                    color,
                    alpha: materialConnection(new Value({parameters: {inputs: {}, settings: {value: 1}}}), "value"),
                }
            },
            hash: hashObject({
                type: "ExtractMaterialSlot",
                material: material.uniqueId,
                slot,
            }),
        },
    }
}

export class ExtractMaterialSlot implements NodeClassImpl<ExtractMaterialSlotDescriptorType, typeof ExtractMaterialSlot> {
    static descriptor = extractMaterialSlotDescriptor
    static uniqueName = "ExtractMaterialSlot"
    material!: Inlet<MaterialGraph>
    slot!: Inlet<TransformMaterialSlotType>
    imageGeneratorForSlot!: Outlet<ImageGenerator>
    sceneManager!: Inlet<ISceneManager>

    run() {
        const material = this.material
        const slot = this.slot
        const sceneManager = this.sceneManager
        if (material === NotReady || slot === NotReady || sceneManager === NotReady) {
            this.imageGeneratorForSlot.emitIfChanged(NotReady)
            return
        }

        this.imageGeneratorForSlot.emitIfChanged(extractMaterialSlotFn(material, slot))
    }
}
